// The rules of DateField (date-field.tsx), exactly as JarvisDateInput had them: its own `dateParser` on top of
// Mantine 7's DateInput (typing, validity, `fixOnBlur`, `preserveTime`). date-field-logic.test.ts runs each rule
// against the verbatim originals.
import { format, startOfDay } from 'date-fns';

/** What DateField shows and what a person types: `21-10-2026`. */
export const DATE_FIELD_FORMAT = 'dd-MM-yyyy';

/**
 * The display formats DateField offers, named as JarvisDateInput's `valueFormat` was (dayjs tokens), mapped to
 * date-fns. `DD-MM-YYYY` is the default; the engine's BL dates and Dokumen date showed `YYYY-MM-DD`.
 */
export const DATE_FIELD_FORMATS = {
  'DD-MM-YYYY': DATE_FIELD_FORMAT,
  'YYYY-MM-DD': 'yyyy-MM-dd',
} as const;
export type DateFieldFormat = keyof typeof DATE_FIELD_FORMATS;

/**
 * The shown text of a value: `21-10-2026` (or `2026-10-21` with `YYYY-MM-DD`), or '' without one. The format is the
 * display only: typing is read by `parseTypedDate` whatever the format, as JarvisDateInput's parser did (so a typed
 * `2026-10-21` goes to the browser's own parser, which reads a date-only ISO text as UTC midnight).
 */
export function formatDateField(value: Date | null | undefined, displayFormat: DateFieldFormat = 'DD-MM-YYYY'): string {
  return value ? format(value, DATE_FIELD_FORMATS[displayFormat]) : '';
}

/**
 * JarvisDateInput's parser: `DD-MM-YYYY` (two-digit day and month, four-digit year, surrounding spaces ignored) as a
 * local date, with no clamping (31-02-2026 is 3 March); anything else goes to the browser's own `new Date(text)`.
 * The result can be an Invalid Date; `isDateValid` decides.
 */
export function parseTypedDate(input: string): Date {
  const match = input.trim().match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (match) {
    const [, day, month, year] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  return new Date(input);
}

/** Mantine's rule: a real date, not after `maxDate` and not before `minDate`, compared by calendar day. */
export function isDateValid({
  date,
  minDate,
  maxDate,
}: {
  date: Date | null | undefined;
  minDate?: Date;
  maxDate?: Date;
}): boolean {
  if (date == null) return false;
  if (Number.isNaN(date.getTime())) return false;
  if (maxDate && startOfDay(date).getTime() > startOfDay(maxDate).getTime()) return false;
  if (minDate && startOfDay(date).getTime() < startOfDay(minDate).getTime()) return false;
  return true;
}

export type TypedResult = { kind: 'set'; date: Date } | { kind: 'clear' } | { kind: 'keep' };

/**
 * What typing `text` does to the value, on every keystroke: an empty field clears it only when `clearable`; a text
 * that parses to a valid date sets it at once; anything else keeps the old value (the text stays as typed until
 * blur puts the value's own text back).
 */
export function typedValue(
  text: string,
  { clearable = false, minDate, maxDate }: { clearable?: boolean; minDate?: Date; maxDate?: Date } = {}
): TypedResult {
  if (text.trim() === '' && clearable) return { kind: 'clear' };
  const date = parseTypedDate(text);
  return isDateValid({ date, minDate, maxDate }) ? { kind: 'set', date } : { kind: 'keep' };
}

/** A day picked in the calendar keeps the clock of the old value (Mantine's `preserveTime`). */
export function withTimeOf(original: Date | null | undefined, day: Date): Date {
  if (!original || !day) return day;
  const result = new Date(day);
  result.setHours(original.getHours());
  result.setMinutes(original.getMinutes());
  result.setSeconds(original.getSeconds());
  result.setMilliseconds(original.getMilliseconds());
  return result;
}

/**
 * Mantine's clear button (DateInput `clearable`): shown while there is a value. DateField draws it only when the page
 * opts in (`clearLabel`), and not while disabled.
 */
export function showsClearButton({
  clearable,
  clearLabel,
  value,
  disabled,
}: {
  clearable?: boolean;
  clearLabel?: string;
  value: Date | null | undefined;
  disabled?: boolean;
}): boolean {
  return Boolean(clearable && clearLabel && value && !disabled);
}

/**
 * What a calendar click does: a day sets the value (keeping the old clock); a click on the selected day, which the
 * calendar reports as no day, clears it when `deselect` is on (Mantine's `allowDeselect`, which follows `clearable`)
 * and otherwise changes nothing (`undefined`).
 */
export function calendarPick(
  original: Date | null | undefined,
  day: Date | undefined,
  deselect: boolean
): Date | null | undefined {
  if (day) return withTimeOf(original, day);
  return deselect ? null : undefined;
}
